<script lang="ts">
  import FormInput from "../../common/form/FormInputWithSurroundError.svelte";
  import Icon from "../../common/icons/Icon.svelte";
  import NavigatorLinkWrapper from "../../common/link/NavigatorLinkWrapper.svelte";
  import Button from "../../common/form/Button.svelte";
  import AuthFormHeader from "../common/AuthFormHeader.svelte";
  import AuthFormStyle from "../common/AuthFormStyle.svelte";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-yup";
  import * as yup from "yup";
  import pageUrl from "../../../PageUrl";
  import { sendResetPasswordRequest, handleError } from "../../../app/api";

  let email: string = "";
  let errorMessage: string = "";
  let requestErrorMessage: string = "";
  let isFocus: boolean = false;
  let sendEmailSuccess: boolean = false;
  let isSubmitting: boolean = false;

  const schema = yup.object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Please enter your email"),
  });

  const { form, errors, validate } = createForm({
    extend: validator({ schema }),
    onSubmit: async (values) => {
      if (isSubmitting) return;

      const email: string = values.email;
      isSubmitting = true;

      await validate();
      updateErrorMessage();

      if (!errorMessage) {
        await sendResetPasswordRequest(email, {
          onSuccess: async () => {
            sendEmailSuccess = true;
          },
          onError: async (error) => {
            console.log("sendResetPasswordRequest Error: ", error);
            sendEmailSuccess = false;
            handleError(error, {
              onNoResponseError: () => {
                requestErrorMessage = "Server is unreachable. Please try again in a few."
              },
              onRequestError: () => {
                requestErrorMessage =
                  "We can’t find any account associated with this email. Please verify your email.";
              },
              onTooManyRequest: () => {
                requestErrorMessage = 
                  "You have sent too many reset password requests. Please try again in a few.";
              },
            });
          },
        });
      }

      isSubmitting = false;
    },
  });

  function updateErrorMessage() {
    let errs = $errors;
    errorMessage = errs.email ? errs.email[0] : "";
  }

  async function handleFormSubmit() {
    await validate();
    updateErrorMessage();
  }

  $: if (errorMessage) {
    requestErrorMessage = "";
  }
</script>

<div class="auth-form-container">
  <div class="form-content">
    <AuthFormHeader title="Reset your password" />

    {#if !sendEmailSuccess}
      <form use:form method="POST" on:submit|capture={handleFormSubmit}>
        <div class="description">
          Enter the email associated with your Datadocs account. We’ll send you
          link to reset your password.
        </div>

        <div class="input-field">
          <p>Email Address</p>
          <div class="input-container" class:focus={isFocus}>
            <div class="prefix-icon">
              <Icon icon="input-email" width="20px" height="18px" />
            </div>
            <FormInput
              bind:value={email}
              bind:isFocus
              {errorMessage}
              name="email"
              errorIndicatorIconSize="20px"
              type="email"
              spellcheck="false"
              placeholder="Enter Your Email"
              autocomplete="off"
              disabled={isSubmitting}
            />
          </div>

          {#if requestErrorMessage}
            <div class="recovery-failed-message">
              {requestErrorMessage}
            </div>
          {/if}
        </div>

        <div class="submit-button relative mt-[86px]">
          <Button disabled={isSubmitting}>Continue</Button>

          <!-- {#if isSubmitting}
            <div
              class="absolute left-0 right-0 top-0 bottom-0 rounded flex flex-row items-center justify-center z-10 text-white bg-primary-datadocs-blue"
            >
              <Icon icon="loading" size="24px" />
            </div>
          {/if} -->
        </div>
      </form>
    {:else}
      <div class="email-notification">
        <Icon icon="email-notification" size="56px" />
      </div>

      <div class="success-message text-center">
        Please check your inbox. We have sent a link to reset the password on
        <span>{email}</span>. If it doesn’t arrive, please check your Spam
        folder.
      </div>

      <div class="sign-in-now">
        <NavigatorLinkWrapper
          to={pageUrl.SIGNIN}
          textColor="#F918FF"
          hoverTextColor="#C418FF"
          activeTextColor="#C418FF"
        >
          <span class="font-semibold text-[15px]">Sign In Now</span>
          <Icon
            icon="splide-arrow-right"
            width="16px"
            height="11px"
            fill="none"
          />
        </NavigatorLinkWrapper>
      </div>
    {/if}
  </div>
</div>

<AuthFormStyle />

<style lang="postcss">
  .form-content::before {
    bottom: 20px;
  }

  .description {
    @apply mt-6 mb-6 text-dark-50 text-[13px];
  }

  .success-message {
    @apply mt-4 mb-12 text-dark-50 text-[13px];
  }

  .success-message span {
    @apply font-medium text-dark-200;
  }

  .submit-button :global(button) {
    @apply w-full text-[13px] py-3 px-0;
  }

  .recovery-failed-message {
    @apply absolute left-0 right-0 px-5 py-2.5 rounded-md text-[13px] font-medium text-tertiary-error bg-tertiary-error bg-opacity-10;
    top: calc(100% + 10px);
  }

  .sign-in-now :global(a) {
    @apply flex flex-row items-center justify-center gap-3;
    letter-spacing: 0px;
  }

  .email-notification {
    @apply flex flex-row items-center justify-center mt-10 mb-4 p-5 mx-auto rounded-full bg-primary-indigo-blue bg-opacity-10;
    max-width: fit-content;
  }
</style>
